import { useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Mail, Phone, Linkedin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { person } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, testid, ...rest }) => (
  <label className="block group">
    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">
      {label}
    </div>
    {type === "textarea" ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[color:var(--nn-border)] py-3 text-lg focus:outline-none focus:border-[color:var(--nn-accent)] transition-colors resize-none"
        {...rest}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[color:var(--nn-border)] py-3 text-lg focus:outline-none focus:border-[color:var(--nn-accent)] transition-colors"
        {...rest}
      />
    )}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message received. I'll respond within 48 hours.");
      setForm({ name: "", email: "", company: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try emailing niravk.naik@gmail.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="pt-24 md:pt-32 pb-0 relative" data-testid="contact">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="section-label mb-6">© Get in touch</div>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="h-kinetic text-[15vw] md:text-[11vw] leading-[0.92] mb-16 md:mb-24"
        >
          Let's build <span className="italic font-serif">something</span>
          <br />
          <span className="text-outline">that lasts.</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 flex flex-col gap-6 order-2 md:order-1">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-3">
                Direct
              </div>
              <a
                href={`mailto:${person.email}`}
                data-cursor="hover"
                data-cursor-label="Send email"
                data-testid="contact-email"
                className="flex items-center gap-3 font-serif text-2xl md:text-3xl italic link-underline"
              >
                <Mail size={20} /> {person.email}
              </a>
              <a
                href={`tel:${person.phone.replace(/\s+/g, "")}`}
                data-cursor="hover"
                data-testid="contact-phone"
                className="flex items-center gap-3 mt-3 text-lg text-[color:var(--nn-muted)] hover:text-[color:var(--nn-text)] transition-colors"
              >
                <Phone size={16} /> {person.phone}
              </a>
              <a
                href={person.linkedin}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                data-testid="contact-linkedin"
                className="flex items-center gap-3 mt-2 text-lg text-[color:var(--nn-muted)] hover:text-[color:var(--nn-text)] transition-colors"
              >
                <Linkedin size={16} /> LinkedIn
                <ArrowUpRight size={14} />
              </a>
            </div>

            <div className="pt-6 border-t border-[color:var(--nn-border)]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">
                Based in
              </div>
              <div className="font-serif text-2xl">{person.location}</div>
              <div className="text-sm text-[color:var(--nn-muted)] mt-1">
                Working remotely across UTC-8 → UTC+5:30
              </div>
            </div>

            <div className="pt-6 border-t border-[color:var(--nn-border)]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">
                Current status
              </div>
              <div className="flex items-center gap-2 font-serif text-2xl">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Booking Q1 – Q2 2026
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="md:col-span-7 order-1 md:order-2 grid grid-cols-1 md:grid-cols-2 gap-8"
            data-testid="contact-form"
          >
            <Field label="Your Name *" name="name" value={form.name} onChange={onChange} testid="input-name" />
            <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="input-email" />
            <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} testid="input-company" />
            <Field label="Budget range (optional)" name="budget" value={form.budget} onChange={onChange} testid="input-budget" placeholder="e.g. $5K – $15K" />
            <div className="md:col-span-2">
              <Field label="Tell me about your project *" name="message" type="textarea" value={form.message} onChange={onChange} testid="input-message" />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[color:var(--nn-muted)] max-w-xs">
                I'll respond within 48 hours with a scoping call proposal.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-cursor="hover"
                data-cursor-label="Send"
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[color:var(--nn-text)] text-[color:var(--nn-bg)] font-medium hover:bg-[color:var(--nn-accent)] hover:text-white transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
                <Send size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-24 md:mt-32 border-t border-[color:var(--nn-border)] py-8 md:py-10 overflow-hidden mask-fade-x">
        <Marquee gradient={false} speed={30}>
          <div className="flex items-center gap-8 mx-6">
            <span className="h-kinetic text-6xl md:text-8xl italic">&gt;95% Client retention</span>
            <span className="w-2 h-2 rounded-full bg-[color:var(--nn-accent)]" />
            <span className="h-kinetic text-6xl md:text-8xl">Contact me</span>
            <span className="w-2 h-2 rounded-full bg-[color:var(--nn-accent)]" />
            <span className="h-kinetic text-6xl md:text-8xl italic">5+ years of craft</span>
            <span className="w-2 h-2 rounded-full bg-[color:var(--nn-accent)]" />
            <span className="h-kinetic text-6xl md:text-8xl">Toronto, CA</span>
            <span className="w-2 h-2 rounded-full bg-[color:var(--nn-accent)]" />
          </div>
        </Marquee>
      </div>
    </section>
  );
};

export default Contact;
